import { useEffect, useState } from "react";
import { Power } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const AdminSiteClosed = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings/closed").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/closed", { enabled: s.enabled, mode: s.mode, reopen_date: s.reopen_date || null, message: s.message });
      setS(data);
      toast.success(data.active ? (data.mode === "holiday" ? "Holiday mode on: orders paused" : "Website is now closed to customers") : "Website is open");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-12" data-testid="admin-site-closed">
      <h2 className="font-display font-bold text-xl flex items-center gap-2 mb-1"><Power className="w-5 h-5 text-primary" /> Holiday Mode / Close Website</h2>
      <p className="text-sm text-muted-foreground mb-4">Pause orders while you're away or updating the site. It switches off automatically on the reopening date.</p>
      <div className="bg-card border border-border rounded-sm p-6 space-y-4 max-w-2xl">
        <div className="flex items-center gap-3">
          <span className={`text-xs font-bold uppercase px-2 py-1 rounded-sm ${s.active ? "bg-destructive/10 text-destructive" : "bg-emerald-500/10 text-emerald-700"}`} data-testid="site-closed-status">
            {s.active ? (s.mode === "holiday" ? "Holiday mode on" : "Closed now") : "Open now"}
          </span>
        </div>
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input type="checkbox" checked={!!s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} className="accent-primary w-4 h-4" data-testid="site-closed-toggle" />
          <span className="text-sm font-medium">Switch on (pause the shop)</span>
        </label>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="site-closed-mode">
          {[["holiday", "Holiday mode", "Customers can browse, but Add to Basket and checkout are paused. A banner shows your return date."],
            ["closed", "Fully closed", "Customers only see the \"We're updating our website\" page."]].map(([v, t, d]) => (
            <label key={v} className={`border rounded-sm p-3 cursor-pointer transition-colors ${s.mode === v ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`site-mode-${v}`}>
              <span className="flex items-center gap-2 text-sm font-semibold">
                <input type="radio" name="site-mode" checked={s.mode === v} onChange={() => setS({ ...s, mode: v })} className="accent-primary" /> {t}
              </span>
              <span className="block mt-1 text-xs text-muted-foreground">{d}</span>
            </label>
          ))}
        </div>
        <label className="block">
          <span className="text-sm font-medium">Reopen on <span className="text-muted-foreground font-normal">(opens automatically from midnight, UK time)</span></span>
          <input type="date" value={s.reopen_date || ""} onChange={(e) => setS({ ...s, reopen_date: e.target.value })} data-testid="site-closed-date"
            className="mt-1 block border border-border rounded-sm px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Message <span className="text-muted-foreground font-normal">(shown on the fully closed page)</span></span>
          <textarea rows={3} maxLength={300} value={s.message} onChange={(e) => setS({ ...s, message: e.target.value })} data-testid="site-closed-message-input"
            className="mt-1 w-full border border-border rounded-sm px-3 py-2 text-sm" />
        </label>
        <button onClick={save} disabled={busy} data-testid="site-closed-save"
          className="bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-sm hover:bg-primary/90 disabled:opacity-50 transition-colors">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
};
